import React from 'react'
import Helmet from 'react-helmet'
import { Location } from '@reach/router'
import PropTypes from 'prop-types'
import { StaticQuery, graphql } from 'gatsby'
import _ from 'lodash'

function SEO({ description, title, meta, keywords }) {
  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              title {
                lang
                text
              }
              description {
                lang
                text
              }
              image
              author
              lang
            }
          }
          allMetaYaml {
            edges {
              node {
                page {
                  name
                  pagetitle {
                    lang
                    text
                  }
                  description {
                    lang
                    text
                  }
                  keywords
                }
              }
            }
          }
        }
      `}
      render={({ site: { siteMetadata: seo }, allMetaYaml: { edges } }) => {
        const lang = seo.lang
        const image = seo.image

        const findLang = titles => {
          const node = _.find(titles, t => {
            return t.lang === lang
          })
          return node ? node.text : ''
        }

        return (
          <Location>
            {lprops => {
              /* ---[ Get path of page currently being rendered ]--- */
              const path = lprops.location.pathname.endsWith('/')
                ? ['index']
                : lprops.location.pathname.split('/')
              const pagename = path[path.length - 1]
              const pagemeta = _.find(edges, e => {
                // returns an edge object
                return e.node.page.name === pagename
              })
              const page = pagemeta ? pagemeta.node.page : undefined

              /* ---[ Set title tag ]--- */
              if (_.isObject(title)) {
                // use inline title of correct language
                title = title[lang]
              } else if (_.isString(title)) {
                // use the inline title as is (not recommended)
              } else if (page && page.pagetitle) {
                // fall back to meta.yml
                const t = findLang(page.pagetitle)
                if (t) {
                  title = t
                } else {
                  // fall back to default site wide title
                  title = findLang(seo.title)
                }
              } else {
                // fall back to default site wide title
                title = findLang(seo.title)
              }

              /* ---[ Set desription tag ]--- */
              if (_.isObject(description)) {
                // use inline description of correct language
                description = description[lang]
              } else if (_.isString(description)) {
                // use the inline description as is (not recommended)
              } else if (page && page.description) {
                // fall back to meta.yml
                const d = findLang(page.description)
                if (d) {
                  description = d
                } else {
                  // fall back to default site wide description
                  description = findLang(seo.description)
                }
              } else {
                description = findLang(seo.description)
              }

              /* ---[ Set keywords tag ]--- */
              if (page && page.keywords) {
                if (keywords.length > 0) {
                  keywords.concat(page.keywords)
                } else {
                  keywords = page.keywords
                }
              }

              return (
                <Helmet
                  htmlAttributes={{ lang }}
                  title={title}
                  meta={[
                    { name: 'description', content: description },
                    { property: 'og:title', content: title },
                    { property: 'og:description', content: description },
                    { property: 'og:image', content: image },
                    { property: 'og:type', content: 'website' },
                    { name: 'twitter:card', content: 'summary_large_image' },
                    { name: 'twitter:title', content: title },
                    { name: 'twitter:description', content: description },
                    { name: 'twitter:image', content: image },
                  ]
                    .concat(
                      keywords.length > 0
                        ? { name: 'keywords', content: keywords.join(',') }
                        : []
                    )
                    .concat(meta)}
                />
              )
            }}
          </Location>
        )
      }}
    />
  )
}

SEO.defaultProps = {
  meta: [],
  keywords: [],
}

SEO.propTypes = {
  description: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
}

export default SEO
